import { useEffect, useState } from "react";
import { Loader2, Plus, Trash2, ArrowUp } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Input } from "../ui/input";
import { api, apiError } from "../../lib/api";
import { RouteTools, reverseRoute } from "./RouteTools";

const CATS = ["Semicama", "Coche cama", "Suite"];
const toHM = (m) => `${String(Math.floor(m / 60)).padStart(2, "0")}:${String(m % 60).padStart(2, "0")}`;
const fromHM = (s) => { const [h, m] = s.split(":").map(Number); return (h || 0) * 60 + (m || 0); };
const uid = () => Math.random().toString(16).slice(2, 10);

const FareMatrix = ({ route, setRoute }) => {
  const [cat, setCat] = useState(CATS[0]);
  const st = route.stops;
  const get = (a, b) => route.fares.find((f) => f.from_id === a && f.to_id === b && f.category === cat)?.price ?? "";
  const set = (a, b, v) => {
    const rest = route.fares.filter((f) => !(f.from_id === a && f.to_id === b && f.category === cat));
    setRoute({ ...route, fares: v === "" ? rest : [...rest, { from_id: a, to_id: b, category: cat, price: Number(v) }] });
  };
  return (
    <div>
      <div className="mb-2 flex flex-wrap items-center gap-2">
        <span className="font-mono text-[11px] uppercase tracking-[0.15em] text-[var(--muted-ink)]">Tarifas por tramo ·</span>
        {CATS.map((c) => <button key={c} type="button" onClick={() => setCat(c)} data-testid={`fare-cat-${c}`} className={`rounded-full px-3 py-1.5 text-xs font-bold ${cat === c ? "bg-[var(--forest)] text-white" : "bg-[var(--paper)]"}`}>{c}</button>)}
      </div>
      <div className="max-h-[360px] overflow-auto rounded-xl border border-black/10" data-lenis-prevent>
        <table className="text-xs">
          <thead className="sticky top-0 bg-[var(--paper)]"><tr><th className="p-2 text-left">Desde \ Hasta</th>{st.slice(1).map((s) => <th key={s.id} className="min-w-[90px] p-2 text-left font-semibold">{s.name.split(" · ")[0]}</th>)}</tr></thead>
          <tbody>
            {st.slice(0, -1).map((a, i) => (
              <tr key={a.id} className="border-t border-black/5">
                <td className="sticky left-0 bg-white p-2 font-semibold">{a.name.split(" · ")[0]}</td>
                {st.slice(1).map((b, j) => (
                  <td key={b.id} className="p-1">{j + 1 > i ? <input type="number" min={0} value={get(a.id, b.id)} onChange={(e) => set(a.id, b.id, e.target.value)} data-testid={`fare-${i}-${j + 1}`} className="h-8 w-[84px] rounded border border-black/10 px-1" /> : null}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="mt-1 text-xs text-[var(--muted-ink)]">Dejá vacío un tramo para no venderlo en esa categoría.</p>
    </div>
  );
};

const RouteDialog = ({ open, route: initial, onClose, onSaved }) => {
  const [route, setRoute] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { setRoute(initial ? JSON.parse(JSON.stringify(initial)) : { name: "", stops: [{ id: uid(), name: "", city: "", offset_min: 0, km: 0 }, { id: uid(), name: "", city: "", offset_min: 60, km: 50 }], fares: [] }); }, [initial, open]);
  if (!route) return null;
  const setStop = (i, k, v) => setRoute({ ...route, stops: route.stops.map((s, j) => (j === i ? { ...s, [k]: v } : s)) });
  const move = (i) => { const s = [...route.stops]; [s[i - 1], s[i]] = [s[i], s[i - 1]]; setRoute({ ...route, stops: s }); };
  const remove = (i) => { const id = route.stops[i].id; setRoute({ ...route, stops: route.stops.filter((_, j) => j !== i), fares: route.fares.filter((f) => f.from_id !== id && f.to_id !== id) }); };
  const save = async () => {
    setSaving(true);
    try {
      const { id, ...body } = route;
      if (id) await api.put(`/admin/routes/${id}`, body); else await api.post("/admin/routes", body);
      onSaved();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="route-editor" className="max-h-[94vh] max-w-5xl overflow-y-auto rounded-3xl [&>*]:min-w-0" data-lenis-prevent>
        <DialogHeader><DialogTitle className="font-display text-3xl">Recorrido</DialogTitle></DialogHeader>
        <Input data-testid="route-name" placeholder="Nombre (ej. Buenos Aires → Tucumán)" value={route.name} onChange={(e) => setRoute({ ...route, name: e.target.value })} className="h-11 rounded-xl" />
        <div className="space-y-2">
          <div className="hidden grid-cols-[1fr_1fr_90px_80px_70px] gap-2 px-1 font-mono text-[10px] uppercase tracking-[0.15em] text-[var(--muted-ink)] md:grid"><span>Parada</span><span>Ciudad (para la búsqueda)</span><span>+hh:mm</span><span>Km</span><span /></div>
          {route.stops.map((s, i) => (
            <div key={s.id} className="grid grid-cols-2 gap-2 md:grid-cols-[1fr_1fr_90px_80px_70px]">
              <Input data-testid={`stop-name-${i}`} placeholder="Parada" value={s.name} onChange={(e) => setStop(i, "name", e.target.value)} className="h-10" />
              <Input data-testid={`stop-city-${i}`} placeholder="Ciudad" value={s.city} onChange={(e) => setStop(i, "city", e.target.value)} className="h-10" />
              <Input data-testid={`stop-offset-${i}`} value={toHM(s.offset_min)} onChange={(e) => setStop(i, "offset_min", fromHM(e.target.value))} className="h-10 font-mono" />
              <Input data-testid={`stop-km-${i}`} type="number" value={s.km} onChange={(e) => setStop(i, "km", Number(e.target.value))} className="h-10" />
              <div className="flex gap-1">
                {i > 0 && <button type="button" onClick={() => move(i)} aria-label="Subir" className="rounded-lg border border-black/10 p-2"><ArrowUp className="h-4 w-4" /></button>}
                <button type="button" onClick={() => remove(i)} aria-label="Quitar" className="rounded-lg border border-black/10 p-2 text-red-700"><Trash2 className="h-4 w-4" /></button>
              </div>
            </div>
          ))}
          <button type="button" onClick={() => { const last = route.stops[route.stops.length - 1]; setRoute({ ...route, stops: [...route.stops, { id: uid(), name: "", city: "", offset_min: (last?.offset_min ?? 0) + 60, km: (last?.km ?? 0) + 50 }] }); }} data-testid="stop-add" className="inline-flex items-center gap-2 rounded-full bg-[var(--paper)] px-4 py-2 text-sm font-bold"><Plus className="h-4 w-4" /> Agregar parada</button>
          <p className="text-xs text-[var(--muted-ink)]">+hh:mm = tiempo desde la salida (ej. 20:10 para llegar al día siguiente a las 14:00 saliendo 17:50).</p>
        </div>
        <RouteTools route={route} setRoute={setRoute} cats={CATS} />
        <FareMatrix route={route} setRoute={setRoute} />
        <button onClick={save} disabled={saving} data-testid="route-save" className="flex h-12 items-center justify-center gap-2 rounded-xl bg-[var(--forest)] font-bold text-white">{saving && <Loader2 className="h-4 w-4 animate-spin" />} Guardar recorrido</button>
      </DialogContent>
    </Dialog>
  );
};

export const RoutesPanel = () => {
  const [items, setItems] = useState([]);
  const [edit, setEdit] = useState(undefined);
  const load = () => api.get("/admin/routes").then((r) => setItems(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  useEffect(() => { load(); }, []);
  return (
    <div data-testid="routes-panel">
      <button onClick={() => setEdit(null)} data-testid="routes-add" className="rounded-full bg-[var(--ink)] px-5 py-3 text-sm font-bold text-white">+ Nuevo recorrido</button>
      <p className="mt-3 max-w-2xl text-sm text-[var(--muted-ink)]">Cargá cada recorrido con sus paradas, kilómetros y tarifa por tramo. Podés pegar las paradas desde Excel, calcular las tarifas por km y después ajustar cada tramo a mano. “Crear la vuelta” arma el recorrido inverso con las mismas tarifas.</p>
      <div className="mt-5 space-y-3">
        {items.map((r) => (
          <div key={r.id} className="flex flex-wrap items-center justify-between gap-3 rounded-2xl bg-white p-5 ring-1 ring-black/5">
            <div>
              <p className="font-display-up text-lg">{r.name}</p>
              <p className="text-sm text-[var(--muted-ink)]">{r.stops.length} paradas · {r.stops[r.stops.length - 1]?.km} km · {r.fares.length} tarifas cargadas</p>
            </div>
            <div className="flex gap-2">
              <button onClick={() => setEdit(r)} data-testid={`route-edit-${r.id}`} className="rounded-full border border-black/10 px-4 py-2 text-sm font-semibold">Editar paradas y tarifas</button>
              <button onClick={() => setEdit(reverseRoute(r))} data-testid={`route-reverse-${r.id}`} className="rounded-full border border-black/10 px-4 py-2 text-sm font-semibold">Crear la vuelta</button>
              <button onClick={async () => { if (window.confirm("¿Eliminar recorrido?")) { await api.delete(`/admin/routes/${r.id}`); load(); } }} className="rounded-full border border-black/10 px-3 py-2 text-sm text-red-700">Eliminar</button>
            </div>
          </div>
        ))}
      </div>
      <RouteDialog open={edit !== undefined} route={edit} onClose={() => setEdit(undefined)} onSaved={() => { setEdit(undefined); load(); }} />
    </div>
  );
};
